import React from 'react';
import styled from 'styled-components';

import Button from '../../../components/Button/Button';
import TextInput from '../../../components/Inputs/TextInput';
import SelectInput from '../../../components/SelectInput/SelectInput';
import useScreenQuery from '../../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import ActiveLimits from './ActiveLimits';
import LimitChangeConfirmModal from './LimitChangeConfirmModal';

const initialFormState = {
	durationId: '',
	amount: '',
};

const PortalLimit = ({
	hasAmount,
	activeLimits,
	name,
	instructions,
	description,
	errorFeedback,
	onRemove,
	onAdd,
	pendingLimits,
	resetErrors,
	options,
	isLoading,
	limitId,
}: PortalLimitProps) => {
	const { isSmall } = useScreenQuery();
	const [showConfirmModal, setShowConfirmModal] = React.useState(false);
	const [formState, setFormState] = React.useState<{
		durationId: number | string;
		amount: string;
	}>(initialFormState);

	const canAdd = formState.amount !== '' && formState.durationId !== undefined;

	const handleAdd = React.useCallback(() => {
		if (typeof formState.durationId === 'string' || !formState.durationId || formState.amount === '') return;

		onAdd(limitId, formState.durationId, Math.round(parseFloat(formState.amount) * 100));
		setShowConfirmModal(false);
	}, [onAdd, limitId, formState]);

	// reset form state when limitId (selected tab) changes
	React.useEffect(() => {
		setFormState(initialFormState);
	}, [setFormState, limitId]);

	// resetting form error when formstate changes
	React.useEffect(resetErrors, [resetErrors, formState]);

	React.useEffect(() => {
		if (!isLoading) setShowConfirmModal(false);
	}, [isLoading]);

	return (
		<PlayupThemeProvider>
			<LimitsWrapper isSmallScreen={isSmall}>
				{activeLimits.length > 0 && (
					<ActiveLimits
						pendingLimits={pendingLimits}
						activeLimits={activeLimits}
						limitName={name}
						onRemove={onRemove}
						isLoading={isLoading}
					/>
				)}
				{typeof instructions === 'string' ? <p>{instructions}</p> : instructions}
				<hr />
				{typeof description === 'string' ? <p>{description}</p> : description}
				<div className="spacer">
					<SelectInput
						kind="white"
						label={`Limit my ${name} to:`}
						value={formState.durationId}
						placeholder="Select a duration"
						onChange={(e) => {
							setFormState({
								durationId: parseInt(e.target.value, 10),
								amount: formState.amount,
							});
						}}
					>
						{options.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label ?? option.value}
							</option>
						))}
					</SelectInput>
				</div>
				{hasAmount && (
					<div className="spacer">
						<TextInput
							kind="white"
							type="number"
							label={`${name} limit amount`}
							placeholder="0.00"
							value={formState.amount}
							onChange={(e) => {
								setFormState({
									durationId: formState.durationId,
									amount: e.target.value,
								});
							}}
						/>
					</div>
				)}
				<div className="spacer">
					<Button
						kind="white"
						onClick={() => setShowConfirmModal(true)}
						disabled={!canAdd || isLoading}
						isLoading={isLoading}
					>
						Set {name} limit
					</Button>
				</div>
				{errorFeedback && <p className="error">{errorFeedback}</p>}
			</LimitsWrapper>

			<LimitChangeConfirmModal
				isVisible={showConfirmModal}
				isLoading={isLoading}
				onCancel={() => setShowConfirmModal(false)}
				onConfirm={handleAdd}
				message="add"
			/>
		</PlayupThemeProvider>
	);
};

export default PortalLimit;

export interface PortalLimitProps {
	hasAmount?: boolean;
	activeLimits: Array<PortalUserLimit>;
	pendingLimits?: Array<PortalUserLimitRequest>;
	description: string;
	errorFeedback?: string | null;
	instructions?: React.ReactNode;
	name: string;
	options: Array<{ value: number; label: string }>;
	onRemove: (typeId: number, durationId: number) => void;
	onAdd: (typeId: number, durationId: number, limit: number) => void;
	isLoading?: boolean;
	limitId: number;
	resetErrors: () => void;
}

export interface PortalLimitItem {
	amount?: string;
	displayAmount?: string;
	duration: string;
	set: string;
}

const LimitsWrapper = styled.div<{ isSmallScreen: boolean }>`
	background-color: ${({ theme }) => theme.colors.white};
	padding: 1rem 2rem;
	${({ isSmallScreen }) => !isSmallScreen && 'max-width: 752px; margin: 30px auto 0;'}

	p {
		margin-bottom: 0.5rem;
		line-height: 1.3;
		color: black;

		&.error {
			text-align: center;
			color: red;
		}
	}

	.spacer {
		margin-bottom: 1.5rem;
	}
	hr {
		margin: 1rem 0;
		border-color: ${({ theme }) => theme.colors.black};
	}
`;
